import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  addSpreadsheetAccess,
  removeSpreadsheetAccess,
  setSpreadsheetCheckingAccess,
  clearAccessCheckTrigger,
  setSpreadsheetAccessChecked,
  clearPendingAuthCheck,
  setPendingColumnOptionsRequest,
  updateRecentlyGrantedAccessRetry,
  clearRecentlyGrantedAccess,
} from '../../lib/redux/sheets/slice';
import { useSendWindowMessages } from './useSendWindowMessages';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';

export function useSpreadsheetAuth() {
  const dispatch = useDispatch();
  const { sendAuthStatus } = useSendWindowMessages();
  const { makeApiCall } = useSpreadsheetApiCall();
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const windowMessages = useSelector((state: RootState) => state.googleSheets.windowMessages);
  const spreadsheetData = useSelector((state: RootState) => state.googleSheets.spreadsheetData);
  
  const currentSpreadsheetId = windowMessages.latestSpreadsheetIdRequested;
  const pendingAuthCheck = windowMessages.pendingAuthCheck;
  const recentlyGrantedAccess = windowMessages.recentlyGrantedAccess;
  const tokenConfig = auth.tokenConfig;
  const triggerAccessCheckForId = spreadsheetData.triggerAccessCheckFor;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Retry delays: 500ms, 1s, 3s, then fail
  const RETRY_DELAYS = [500, 1000, 3000];

  // Function to check access for recently granted spreadsheet with retry logic
  const checkRecentlyGrantedAccess = useCallback(async (spreadsheetId: string, retryAttempt: number) => {
    if (!tokenConfig) {
      return;
    }

    try {
      dispatch(updateRecentlyGrantedAccessRetry({
        retryAttempt,
        isRetrying: true,
        lastError: undefined
      }));

      const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title`;
      const result = await makeApiCall({ url });
      
      const hasAccess = result.success;
      
      if (hasAccess) {
        dispatch(addSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, true);
        dispatch(setPendingColumnOptionsRequest({ spreadsheetId }));
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: undefined
        }));
        setTimeout(() => {
          dispatch(clearRecentlyGrantedAccess());
        }, 2000);
        
      } else if (retryAttempt < RETRY_DELAYS.length) {
        // Retry with next delay
        const delay = RETRY_DELAYS[retryAttempt];
        const errorMsg = `Access check failed (${result.status}). Retrying in ${delay}ms...`;
        
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: true,
          lastError: errorMsg
        }));
        
        timeoutRef.current = setTimeout(() => {
          checkRecentlyGrantedAccess(spreadsheetId, retryAttempt + 1);
        }, delay);
        
      } else {
        // Final failure
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: `Failed to verify access after ${RETRY_DELAYS.length + 1} attempts`
        }));
      }
      
    } catch (error) {
      if (retryAttempt < RETRY_DELAYS.length) {
        // Retry on error
        const delay = RETRY_DELAYS[retryAttempt];
        const errorMsg = `Network error. Retrying in ${delay}ms...`;
        
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: true,
          lastError: errorMsg
        }));
        
        timeoutRef.current = setTimeout(() => {
          checkRecentlyGrantedAccess(spreadsheetId, retryAttempt + 1);
        }, delay);
        
      } else {
        // Final error
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: `Network error after ${RETRY_DELAYS.length + 1} attempts`
        }));
      }
    }
  }, [tokenConfig, dispatch, sendAuthStatus, makeApiCall]);

  // Simple access check function for other use cases
  const checkSpreadsheetAccess = useCallback(async (spreadsheetId: string) => {
    if (!tokenConfig || !spreadsheetId) return;
    
    try {
      dispatch(setSpreadsheetCheckingAccess({ id: spreadsheetId, checking: true }));
      
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title`;
      const result = await makeApiCall({ url });
      
      const hasAccess = result.success;
      if (result.status === 401) {
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
      } else if (hasAccess) {
        dispatch(addSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, true);
      } else {
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
      }
    } catch (error) {
      dispatch(removeSpreadsheetAccess(spreadsheetId));
      sendAuthStatus(spreadsheetId, false);
    } finally {
      dispatch(setSpreadsheetCheckingAccess({ id: spreadsheetId, checking: false }));
      dispatch(setSpreadsheetAccessChecked({ id: spreadsheetId, checked: true }));
    }
  }, [tokenConfig, dispatch, sendAuthStatus, makeApiCall]);

  // Watch for recently granted access and start retry flow
  useEffect(() => {
    if (recentlyGrantedAccess && recentlyGrantedAccess.isRetrying && recentlyGrantedAccess.retryAttempt === 0) {
      // Start the retry process
      checkRecentlyGrantedAccess(recentlyGrantedAccess.spreadsheetId, 0);
    }
  }, [recentlyGrantedAccess, checkRecentlyGrantedAccess]);

  // Handle pending auth check requests (for other flows)
  useEffect(() => {
    if (!pendingAuthCheck || !tokenConfig) return;
    checkSpreadsheetAccess(pendingAuthCheck.spreadsheetId);
    dispatch(clearPendingAuthCheck());
  }, [pendingAuthCheck, tokenConfig, checkSpreadsheetAccess, dispatch]);

  // Listen for trigger access check requests (for other flows)
  useEffect(() => {
    if (triggerAccessCheckForId && tokenConfig) {
      checkSpreadsheetAccess(triggerAccessCheckForId);
      dispatch(clearAccessCheckTrigger());
    }
  }, [triggerAccessCheckForId, tokenConfig, checkSpreadsheetAccess, dispatch]);

  // Auto-check access for current spreadsheet (for other flows)
  useEffect(() => {
    if (!currentSpreadsheetId || !tokenConfig) return;
    
    const hasAccess = spreadsheetData.spreadsheetAccess.includes(currentSpreadsheetId);
    const isCheckingAccess = spreadsheetData.spreadsheetIdToCheckingAccess[currentSpreadsheetId];
    const hasBeenChecked = spreadsheetData.spreadsheetIdToAccessChecked[currentSpreadsheetId];
    
    // Only check if we don't have access, aren't currently checking, and haven't been checked yet
    // And we're not in the recently granted access flow
    if (!hasAccess && !isCheckingAccess && !hasBeenChecked && !recentlyGrantedAccess) {
      checkSpreadsheetAccess(currentSpreadsheetId);
    }
  }, [currentSpreadsheetId, tokenConfig, spreadsheetData.spreadsheetAccess, spreadsheetData.spreadsheetIdToCheckingAccess, spreadsheetData.spreadsheetIdToAccessChecked, recentlyGrantedAccess, checkSpreadsheetAccess]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
} 